import { useEffect } from "react";
import { useQueryClient } from "react-query";
import { Box, Button, MenuItem, Stack, Typography } from "@mui/material";

import Layout from "../../templates/Layout";
import FormLayout from "../../Layout/FormLayout";
import {
  UpdateAccountInputType,
  UpdateAccountSchema,
} from "../../../schemas/userSchema";
import { handleError } from "../../../utils/error/handleError";
import { callUpdateMe } from "../../../api/me";
import { meKey } from "../../../hooks/me/meKeys";
import { notify } from "../../../lib/notify";
import { useAppNavigate } from "../../../routes/useAppNavigate";
import { useMeQuery } from "../../../hooks/me/useMe";
import { useFormWithSchema } from "../../../hooks/useFormWithSchema";
import { AppUserRole, getAppUserRoleLabel } from "../../../types/roles";
import LoadingButton from "../../Button/LoadingButton";
import LoadingTextField from "../../Form/LoadingTextField";
import LoadingSelect from "../../Form/LoadingSelect";

const UpdateAccountPage = () => {
  const queryClient = useQueryClient();
  const { meNavigate, backNavigate } = useAppNavigate();
  const {
    data,
    isFetching,
    isError: isQueryError,
    error: queryError,
  } = useMeQuery();
  const { data: loginUser } = useMeQuery();

  useEffect(() => {
    if (isQueryError) {
      handleError(queryError);
    }
  }, [isQueryError, queryError]);

  const onSubmit = async (input: UpdateAccountInputType) => {
    try {
      const res = await callUpdateMe({
        email: input.email,
        userName: input.userName,
      });

      queryClient.setQueryData(meKey.me(), res);

      notify("保存しました。");
      meNavigate.detail();
    } catch (error) {
      handleError(error);
    }
  };

  const {
    formState: { isSubmitting, errors },
    register,
    onSubmitHandler,
  } = useFormWithSchema(
    UpdateAccountSchema,
    onSubmit,
    data ?? {
      email: "",
      userName: "",
    }
  );

  return (
    <Layout title="アカウント設定編集">
      <FormLayout toolbar={<Typography>アカウント設定編集</Typography>}>
        <Box component={"form"} onSubmit={onSubmitHandler} noValidate>
          <Stack spacing={3}>
            <LoadingTextField
              isLoading={isFetching}
              label="ユーザー名"
              {...register("userName")}
              error={!!errors.userName}
              helperText={errors.userName?.message}
              name="userName"
              type="text"
              fullWidth
              size="small"
              disabled={isSubmitting}
            />
            <LoadingTextField
              isLoading={isFetching}
              label="ログインID"
              fullWidth
              size="small"
              value={data?.loginId ?? ""}
              disabled
            />
            <LoadingTextField
              isLoading={isFetching}
              label="メールアドレス"
              {...register("email")}
              error={!!errors.email}
              helperText={errors.email?.message}
              name="email"
              fullWidth
              size="small"
              disabled={
                isSubmitting ||
                !loginUser ||
                loginUser.role === AppUserRole.Worker
              }
            />
            <LoadingSelect
              isLoading={isFetching}
              disabled
              value={"role"}
              size="small"
            >
              <MenuItem value="role">
                {getAppUserRoleLabel(data?.role)}
              </MenuItem>
            </LoadingSelect>
          </Stack>
          <Stack
            direction={"row"}
            justifyContent={"flex-end"}
            sx={{ py: 4 }}
            spacing={2}
          >
            <Button
              onClick={() => backNavigate()}
              variant="outlined"
              sx={{ minWidth: "120px" }}
              disabled={isSubmitting}
            >
              キャンセル
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              sx={{ minWidth: "120px" }}
              disabled={isFetching || isSubmitting}
              isLoading={isSubmitting}
            >
              保存
            </LoadingButton>
          </Stack>
        </Box>
      </FormLayout>
    </Layout>
  );
};

export default UpdateAccountPage;
